import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeSlash, SignIn } from "@phosphor-icons/react";
import { Logo } from "@/components/Logo";
import { isStandalone } from "@/lib/pwa";
import { toast } from "sonner";

export default function Login() {
    const navigate = useNavigate();
    const { login } = useAuth();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        const result = await login(email, password);
        setSubmitting(false);
        if (result.success) {
            toast.success("Welcome back!");
            if (result.user.role === "admin") navigate("/admin");
            else navigate(isStandalone() ? "/time-tracking" : "/dashboard");
        } else {
            toast.error(result.error || "Login failed");
        }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2 bg-[#F8FAFC]">
            {/* Left visual (hidden on mobile) */}
            <div
                className="hidden lg:flex relative overflow-hidden text-white"
                style={{
                    backgroundImage:
                        "linear-gradient(135deg, rgba(0,51,102,0.92), rgba(0,102,217,0.65)), url(https://images.unsplash.com/photo-1680992046626-418f7e910589?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80)",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                }}
            >
                <div className="relative z-10 flex flex-col justify-between p-12 w-full">
                    <div className="flex items-center gap-3">
                        <Logo size={48} />
                        <div className="leading-tight">
                            <div className="font-bold text-xl tracking-tight" style={{ fontFamily: "Outfit" }}>
                                ADVANCE NETWORKS LLC
                            </div>
                            <div className="text-xs uppercase tracking-[0.25em] text-white/70">
                                Internal Technician Portal
                            </div>
                        </div>
                    </div>

                    <div className="space-y-6">
                        <h1
                            className="text-5xl font-bold tracking-tight leading-tight"
                            style={{ fontFamily: "Outfit" }}
                        >
                            Built for the field.
                            <br />
                            <span className="text-[#0066D9]">Engineered for clarity.</span>
                        </h1>
                        <p className="text-white/80 max-w-md text-base leading-relaxed">
                            Access your weekly payment reports, submit requests, and manage your profile —
                            all from one secure, mobile-friendly workspace.
                        </p>
                        <div className="flex items-center gap-6 pt-4 border-t border-white/10">
                            <div>
                                <div className="text-2xl font-bold" style={{ fontFamily: "Outfit" }}>
                                    100%
                                </div>
                                <div className="text-xs text-white/60 uppercase tracking-wider">Mobile ready</div>
                            </div>
                            <div>
                                <div className="text-2xl font-bold" style={{ fontFamily: "Outfit" }}>
                                    PDF
                                </div>
                                <div className="text-xs text-white/60 uppercase tracking-wider">Export</div>
                            </div>
                            <div>
                                <div className="text-2xl font-bold" style={{ fontFamily: "Outfit" }}>
                                    24/7
                                </div>
                                <div className="text-xs text-white/60 uppercase tracking-wider">Access</div>
                            </div>
                        </div>
                    </div>

                    <div className="text-xs text-white/50">
                        © {new Date().getFullYear()} Advance Networks LLC. All rights reserved.
                    </div>
                </div>
            </div>

            {/* Right form */}
            <div className="flex items-center justify-center p-6 sm:p-12">
                <div className="w-full max-w-md">
                    {/* Mobile logo */}
                    <div className="lg:hidden flex items-center gap-2.5 mb-10">
                        <Logo size={40} />
                        <div className="leading-tight">
                            <div className="font-bold text-base text-[#003366]" style={{ fontFamily: "Outfit" }}>
                                ADVANCE NETWORKS
                            </div>
                            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                                Technician Portal
                            </div>
                        </div>
                    </div>

                    <div className="mb-8">
                        <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">
                            Welcome back
                        </div>
                        <h2
                            className="text-3xl sm:text-4xl font-bold text-[#003366]"
                            style={{ fontFamily: "Outfit" }}
                        >
                            Sign in to your account
                        </h2>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
                        <div className="space-y-2">
                            <Label htmlFor="email" className="text-xs uppercase tracking-wider font-semibold text-[#475569]">
                                Email address
                            </Label>
                            <Input
                                id="email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                placeholder="you@advance-net.co"
                                className="h-11 border-[#E2E8F0] focus-visible:ring-[#0066D9]"
                                data-testid="login-email-input"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="password" className="text-xs uppercase tracking-wider font-semibold text-[#475569]">
                                Password
                            </Label>
                            <div className="relative">
                                <Input
                                    id="password"
                                    type={showPassword ? "text" : "password"}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    placeholder="••••••••"
                                    className="h-11 pr-11 border-[#E2E8F0] focus-visible:ring-[#0066D9]"
                                    data-testid="login-password-input"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword((v) => !v)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                    data-testid="toggle-password-visibility"
                                >
                                    {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                                </button>
                            </div>
                            <div className="flex justify-end pt-1">
                                <Link
                                    to="/forgot-password"
                                    className="text-xs font-semibold text-[#0066D9] hover:underline"
                                    data-testid="link-to-forgot-password"
                                >
                                    Forgot password?
                                </Link>
                            </div>
                        </div>

                        <Button
                            type="submit"
                            disabled={submitting}
                            className="w-full h-11 bg-[#0066D9] hover:bg-[#0052ad] text-white font-medium"
                            data-testid="login-submit-button"
                        >
                            {submitting ? "Signing in..." : (<><SignIn size={18} className="mr-2" /> Sign in</>)}
                        </Button>
                    </form>

                    <div className="mt-8 pt-6 border-t border-[#E2E8F0] text-sm text-slate-600">
                        New technician?{" "}
                        <Link
                            to="/register"
                            className="text-[#0066D9] font-semibold hover:underline"
                            data-testid="link-to-register"
                        >
                            Create an account
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
}
